import { z } from "zod";
import { EXTRACTION_CONFIDENCES } from "@/lib/enums";

const SERIAL_MAX = 120;

/**
 * `POST /api/extractions/:photoId/confirm`. A field left out keeps what it was: the suggestion on
 * a first confirm, the PM's earlier correction on a confirmed reading. A value is the PM's correction
 * (serial, make, model or equipment type). An empty one says this photo proves none of that field.
 */
export const confirmExtractionSchema = z.strictObject({
  serial: z.string().trim().max(SERIAL_MAX).optional(),
  make: z.string().trim().max(SERIAL_MAX).optional(),
  model: z.string().trim().max(SERIAL_MAX).optional(),
  equipmentType: z.string().trim().max(SERIAL_MAX).optional(),
});

const fields = {
  equipmentType: z.string().nullable(),
  make: z.string().nullable(),
  model: z.string().nullable(),
  serial: z.string().nullable(),
  confirmedAt: z.string().nullable(),
};

/** What a PM gets: the status to mark it by, and the suggestion beside any correction. */
const reviewedExtractionSchema = z.object({
  ...fields,
  status: z.enum(["suggested", "confirmed", "rejected"]),
  confidence: z.enum(EXTRACTION_CONFIDENCES),
  suggestedSerial: z.string().nullable(),
  suggestedMake: z.string().nullable(),
  suggestedModel: z.string().nullable(),
  suggestedEquipmentType: z.string().nullable(),
});
export type ReviewedExtraction = z.infer<typeof reviewedExtractionSchema>;

/** What an IE gets: confirmed values and nothing of how they were reached. */
const confirmedExtractionSchema = z.object({ ...fields, status: z.literal("confirmed") });

export const extractionResponseSchema = z.object({
  data: z.union([reviewedExtractionSchema, confirmedExtractionSchema]).nullable(),
});
export type ExtractionView = NonNullable<z.infer<typeof extractionResponseSchema>["data"]>;
